import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateSupplierReceiptCashCaches } from "@/lib/supplier-payment-cache";
import { invalidateRecipeCostQueries } from "@/hooks/use-recipes";
import { invalidateItemPurchasingCosts } from "@/lib/item-purchasing-cost-cache";
import type { CreateReceiptReturn, ReceiptReturns, ReturnLineSelection, ReturnPreview, ReturnReceiptSource, SupplierReceiptReturn } from "@/lib/supplier-receipt-return";

export function useSupplierReceiptReturns(workspaceId: string, receiptId: string, enabled = true) {
  return useQuery<ReceiptReturns>({
    queryKey: ["/api/supplier-receipt-returns", workspaceId, "receipt", receiptId],
    enabled: !!workspaceId && !!receiptId && enabled, staleTime: 0,
    queryFn: async () => (await apiRequest("GET", `/api/supplier-receipts/${encodeURIComponent(receiptId)}/returns?${new URLSearchParams({ workspaceId })}`)).json(),
  });
}
export function useSupplierReturnList(workspaceId: string, supplierId?: string) {
  return useQuery<{ returns: SupplierReceiptReturn[] }>({
    queryKey: ["/api/supplier-receipt-returns", workspaceId, "list", supplierId],
    enabled: !!workspaceId, staleTime: 0,
    queryFn: async () => (await apiRequest("GET", `/api/supplier-receipt-returns?${new URLSearchParams({ workspaceId, ...(supplierId ? { supplierId } : {}) })}`)).json(),
  });
}
export function useReturnReceiptSource(workspaceId: string, returnId: string, enabled = true) {
  return useQuery<ReturnReceiptSource | null>({
    queryKey: ["/api/return-receipt-source", workspaceId, returnId],
    enabled: !!workspaceId && !!returnId && enabled, staleTime: 0,
    queryFn: async () => {
      try { return await (await apiRequest("GET", `/api/returns/${encodeURIComponent(returnId)}/supplier-receipt?${new URLSearchParams({ workspaceId })}`)).json(); }
      catch (error) {
        // Legacy supplier returns have no source receipt; other failures must not
        // silently unlock quantity editing or permit shipment without a preview.
        if (error instanceof Error && /^404\b/.test(error.message)) return null;
        throw error;
      }
    },
  });
}
export function useRefreshSupplierReturns() {
  const cache = useQueryClient();
  return async (workspaceId: string) => {
    await Promise.all([
      "/api/supplier-receipt-returns", "/api/return-receipt-source", "/api/returns",
      "/api/supplier-receipts", "/api/supplier-receipt-investigation",
      "/api/inventory", "/api/stock", "/api/inventory-transactions",
      "/api/locations/inventory", "/api/locations/inventory/summary", "/api/locations/inventory/transactions",
      "inventory", "stock", "inventory-transactions", "audit-entity",
    ].map(key => cache.invalidateQueries({ queryKey: [key] })));
    await invalidateSupplierReceiptCashCaches(cache, workspaceId);
    await invalidateRecipeCostQueries(cache, workspaceId);
    await invalidateItemPurchasingCosts(cache, workspaceId);
  };
}
export function usePreviewSupplierReturn(workspaceId: string, receiptId: string) {
  return useMutation<ReturnPreview, Error, { lines: ReturnLineSelection[]; settlementType: "credit" | "cash" }>({
    mutationFn: async data => (await apiRequest("POST", `/api/supplier-receipts/${encodeURIComponent(receiptId)}/returns/preview`, { workspaceId, ...data })).json(),
  });
}
export function useCreateSupplierReturn(receiptId: string) {
  return useMutation<{ id: string; returnNumber: string; status: string; cashPosted: boolean }, Error, CreateReceiptReturn>({
    mutationFn: async data => (await apiRequest("POST", `/api/supplier-receipts/${encodeURIComponent(receiptId)}/returns`, data)).json(),
  });
}